import {
  AddOutlined,
  SearchOff,
  VisibilityOutlined,
} from "@mui/icons-material";
import { Box, Button } from "@mui/material";
import { grey } from "@mui/material/colors";
import { AgGridReact } from "ag-grid-react";
import { isEmpty, isNil, size } from "lodash";
import { forwardRef, useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useColumnTypes } from "../../_src_shared/utils/agGrid/columnTypes/hooks/useColumnTypes";
import StagedApplicationWizard from "../../AuthenticatedDashboard/StagedApplicationWizard";
import AgGridTableWrapper from "../../components/agGrid/AgGridTableWrapper";
import Flx from "../../components/layout/Flx";
import Screen from "../../components/layout/Screen";
import ScreenContent from "../../components/layout/ScreenContent";
import TitledHeader from "../../components/layout/TitledHeader";
import Htag from "../../components/typography/Htag";
import Txt from "../../components/typography/Txt";

const StagedApplicationsScreen = () => {
  const stagedApplications =
    useSelector((state) => state?.profile?.stagedApplications) || [];
  const navigate = useNavigate();
  const [selectedApplication, setSelectedApplication] = useState(null);
  return (
    <Screen
      sx={{
        position: "absolute",
        left: 0,
        width: "100%",
        backgroundColor: "#f5f8fa",
        height: "100%",
      }}
    >
      <TitledHeader
        title={"Staged Applications"}
        description="These are pre-filled financing requests prepared by your loan team. Review the details and click 'View Application' to complete and submit."
      />
      <ScreenContent
        maxWidth={isEmpty(stagedApplications) ? "md" : "xl"}
        sx={{
          width: "100%",
          pb: 14,
          pt: 4,
          // px: isMobile ? 0 : 5,
          margin: "0px auto",
        }}
      >
        {isEmpty(stagedApplications) ? (
          <Box
            sx={{
              my: 2,
              p: 1,
              py: 5,
              border: "1px solid #e2e8f0",
              borderRadius: 2,
              background: "#ffffff",
            }}
          >
            <Flx
              column
              gap={1}
              sx={{ maxWidth: "620px", margin: "0 auto" }}
              center
            >
              <SearchOff
                className="thin"
                sx={{ mb: 2, fontSize: 54, color: grey[400] }}
              />
              <Htag h3>No Staged Applications Found</Htag>
              <Txt secondary center>
                There are no pre-filled applications waiting for your review.
                Talk to your loan officer about preparing a staged application,
                or you can start a new financing request yourself.
              </Txt>
              <Button
                sx={{ mt: 2 }}
                size="large"
                startIcon={<AddOutlined />}
                onClick={() => navigate("/financing-request/create")}
              >
                Create New Financing Request
              </Button>
            </Flx>
          </Box>
        ) : (
          <Flx column g={0.4}>
            <Flx column g={0.4} sx={{ mb: 1 }}>
              <Txt sx={{ fontWeight: 400, fontSize: 14 }} secondary>
                You have <b>{size(stagedApplications)}</b> staged application
                {size(stagedApplications) > 1 ? "s" : ""} waiting for your
                review.
              </Txt>
              {/* <Htag h3>Staged Applications ({size(stagedApplications)})</Htag> */}
              {/* <Txt secondary>
                You have currently have ({size(stagedApplications)}) staged
                application{size(stagedApplications) > 1 ? "s" : ""} waiting for
                your review.
              </Txt> */}
            </Flx>
            <StagedApplicationsTable
              rowData={stagedApplications}
              onRowClicked={(application) =>
                setSelectedApplication(application)
              }
            />
          </Flx>
        )}
      </ScreenContent>
      <StagedApplicationWizard
        show={!isNil(selectedApplication)}
        stagedApplication={selectedApplication}
        onClose={() => setSelectedApplication(null)}
      />
    </Screen>
  );
};

const StagedApplicationsTable = forwardRef(
  ({ quickFilter, rowData, loading, activeId, onRowClicked }, ref) => {
    const columnDefs = useMemo(
      () => [
        {
          headerName: "Actions",
          flex: 0,
          width: 120,
          minWidth: 120,
          cellRenderer: (p) => {
            return <Button startIcon={<VisibilityOutlined />}>Preview</Button>;
          },
        },
        {
          headerName: "Created",
          field: "createdDate",
          type: "date",
          sort: "desc",
          width: 120,
          minWidth: 120,
          flex: 0,
        },
        {
          headerName: "Loan Name",
          field: "loanName",

          // minWidth: 250,
        },

        {
          headerName: "Loan Product",
          field: "loanProductType",
          width: 150,
          minWidth: 150,
        },
        {
          headerName: "Loan Purpose",
          field: "loanPurpose",
          width: 140,
          minWidth: 140,
        },
        {
          headerName: "Base Loan Amount",
          field: "baseLoanAmount",
          width: 170,
          minWidth: 170,

          valueFormatter: (p) =>
            p.value ? `$${Number(p.value).toLocaleString()}` : "",
        },

        {
          headerName: "Properties",
          field: "subjectProperties",
          width: 130,
          minWidth: 130,
          valueGetter: (p) =>
            p.data?.subjectProperties
              ?.map((b) => `${b.address?.fullAddress}`)
              .join(", ") || "",
          tooltipValueGetter: (p) =>
            p.data?.subjectProperties
              ?.map((b) => `${b.address?.fullAddress}`)
              .join(", ") || "",
        },
        {
          headerName: "Borrower",

          width: 180,
          minWidth: 180,
          flex: 0,
          valueGetter: (p) =>
            p.data?.borrowers
              ?.map((b) => `${b.firstName} ${b.lastName}`)
              .join(", ") || "",
          tooltipValueGetter: (p) =>
            p.data?.borrowers
              ?.map((b) => `${b.firstName} ${b.lastName}`)
              .join(", ") || "",
        },
        {
          headerName: "Application Status",
          field: "applicationStatus",
          width: 130,
          minWidth: 130,
        },
        {
          headerName: "Application Status",
          field: "applicationStatus",
          width: 130,
          minWidth: 130,
        },
        // {
        //   headerName: "Borrower Phone",
        //   valueGetter: (p) => p.data?.borrowers?.[0]?.phone,
        //   width: 150,
        //   minWidth: 150,
        //   flex: 0,
        //   valueFormatter: (p) => (p.value ? formatPhone(p.value) : ""),
        // },
        // {
        //   headerName: "Property Type",
        //   valueGetter: (p) => p.data?.subjectProperties?.[0]?.propertyType,
        //   width: 140,
        //   minWidth: 140,
        //   flex: 0,
        // },
        // {
        //   headerName: "Property Address",
        //   valueGetter: (p) =>
        //     p.data?.subjectProperties?.[0]?.address?.fullAddress,
        //   flex: 1,
        //   minWidth: 250,
        // },
        // {
        //   headerName: "Created By",
        //   field: "createdBy",
        //   width: 200,
        //   minWidth: 200,
        //   flex: 0,
        // },
      ],
      [],
    );

    const columnTypes = useColumnTypes();
    const defaultColDef = useMemo(
      () => ({
        flex: 1,
        minWidth: 100,
      }),
      [],
    );

    const handleOnRowClicked = useCallback(
      (props) => {
        const data = props?.data;
        onRowClicked(data);
      },
      [onRowClicked],
    );

    return (
      <AgGridTableWrapper compactTable sx={{ flexGrow: 1, width: "auto" }}>
        <AgGridReact
          rowData={rowData}
          ref={ref}
          columnDefs={columnDefs}
          quickFilterText={quickFilter}
          tooltipShowDelay={100}
          // pagination={true}
          // paginationPageSize={20}
          domLayout="autoHeight"
          // getRowStyle={getRowStyle}
          defaultColDef={defaultColDef}
          onRowClicked={handleOnRowClicked}
          groupDisplayType={"groupRows"}
          columnTypes={columnTypes}
          loading={loading}
        />
      </AgGridTableWrapper>
    );
  },
);
export default StagedApplicationsScreen;
